import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { getAiStatus, suggestForEvent } from '../api/ai.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';

/**
 * "What should we do about this?" on an event (section 13; P9.3): an operator
 * asks a model for a recommendation, which lands on the Suggestions page to be
 * approved or rejected there. Nothing happens from here but the asking.
 */
export function SuggestCard({ eventId }: { eventId: string }): ReactNode {
  const queryClient = useQueryClient();
  const status = useQuery({ queryKey: ['ai-status'], queryFn: getAiStatus });
  const [error, setError] = useState<string | null>(null);
  const ask = useMutation({
    mutationFn: () => suggestForEvent(eventId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['suggestions'] });
    },
    onError: (caught: unknown) => {
      setError(caught instanceof Error ? caught.message : 'The model did not answer.');
    },
  });
  if (status.data?.tasks.incident_analysis !== true) return null;
  const made = ask.data;

  return (
    <Card
      title="Recommendation"
      description="Ask a language model what to do about this incident. A proposed ban waits on the Suggestions page for a person to approve it."
      aside={
        <Button
          busy={ask.isPending}
          busyLabel="Asking…"
          onClick={() => {
            setError(null);
            ask.mutate();
          }}
        >
          {made === undefined ? 'Ask for a recommendation' : 'Ask again'}
        </Button>
      }
    >
      {error !== null && <Alert tone="danger">{error}</Alert>}
      {made !== undefined && (
        <div className="stack">
          <p>
            <strong>{made.title}.</strong> {made.summary}
          </p>
          <p>
            {made.proposedAction !== null
              ? `It proposes banning ${made.proposedAction.ip} on every server. `
              : made.recommendation === 'ignore'
                ? 'It sees nothing to do. '
                : 'It proposes no action. '}
            <Link to="/suggestions">Review it on the Suggestions page</Link>.
          </p>
        </div>
      )}
    </Card>
  );
}
